import React, { useEffect, useState } from "react";
import { X, Train } from "lucide-react";
import NewsletterSignup from "./NewsletterSignup";

/**
 * NewsletterPopup
 * --------------------------------------------------------------------------
 * First-visit popup that surfaces the newsletter after the visitor has been
 * on the site for 20 seconds. Suppression rules:
 *   • never shows again once the visitor dismisses it (X or Esc)
 *   • never shows again once the visitor subscribes (from ANY form on the
 *     site — the NewsletterSignup component fires a `tdf-newsletter-signed-up`
 *     window event on success that we listen for)
 *   • never shows on admin/auth/checkout/course-player routes so it can't
 *     interrupt a live transactional flow
 *   • never shows in the same session it was already scheduled once
 *
 * State lives in localStorage under `tdf_newsletter_popup_state`.
 */
const STORAGE_KEY = "tdf_newsletter_popup_state";
const SIGNUP_KEY = "tdf_newsletter_signed_up";
const DELAY_MS = 20_000;

// Path prefixes on which the popup MUST NOT appear.
const BLOCKLIST = [
  "/admin",
  "/auth",
  "/checkout",
  "/payment",
  "/my-account",
  "/cv-checker/app",
  "/interview-simulator/app",
];

function isSuppressed() {
  try {
    if (localStorage.getItem(SIGNUP_KEY) === "1") return true;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const rec = JSON.parse(raw);
    return rec?.status === "dismissed" || rec?.status === "shown";
  } catch (e) {
    return false;
  }
}

function markStatus(status) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ status, at: new Date().toISOString() })
    );
  } catch (e) {
    /* ignore */
  }
}

function pathIsBlocked() {
  const p = window.location.pathname || "/";
  return BLOCKLIST.some((prefix) => p.startsWith(prefix));
}

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isSuppressed() || pathIsBlocked()) return;
    const t = setTimeout(() => {
      if (isSuppressed() || pathIsBlocked()) return;
      setOpen(true);
      markStatus("shown");
    }, DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    function onSignup() {
      setOpen(false);
      markStatus("subscribed");
    }
    window.addEventListener("tdf-newsletter-signed-up", onSignup);
    return () =>
      window.removeEventListener("tdf-newsletter-signed-up", onSignup);
  }, []);

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  function close() {
    setOpen(false);
    markStatus("dismissed");
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="newsletter-popup-title"
      data-testid="newsletter-popup"
      className="fixed inset-0 z-[1050] flex items-end sm:items-center justify-center bg-black/55 backdrop-blur-sm px-3 py-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="relative w-full max-w-md bg-[#0F1B2E] text-white rounded-2xl overflow-hidden shadow-2xl border border-white/10">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          data-testid="newsletter-popup-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 pt-8 pb-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-slate-200 mb-4">
            <Train className="w-3.5 h-3.5 text-[#E63B2E]" />
            Career update list
          </div>

          <h3
            id="newsletter-popup-title"
            className="tdf-heading text-2xl leading-tight"
          >
            Move closer to the driver&rsquo;s seat.
          </h3>
          <p className="text-sm text-slate-300 mt-2 leading-relaxed">
            Get train driver recruitment updates, preparation tips, application
            guidance and career articles to help you plan your next step.
          </p>

          <div className="mt-5">
            <NewsletterSignup
              source="popup"
              dark
              ctaLabel="Start Preparing"
            />
          </div>

          <p className="text-[11px] text-slate-500 mt-4 leading-snug">
            No spam. Unsubscribe with one click at any time.
          </p>
        </div>
      </div>
    </div>
  );
}
